// src/Modal.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import CustomDatePicker from '../forms/DatePicker';
import { RRule } from 'rrule';
import reservationsWS from '../../services/wsReservations';
import { useTranslation } from 'react-i18next';
import './Modal.css';


// const API_BASE_URL = process.env.REACT_APP_BASE_DOMAIN;
const API_BASE_URL = '';

const toDateTimeString = (date) => moment(date).format('YYYY-MM-DDTHH:mm:ss');
const toDateObj = (val) => val ? new Date(val) : null;

const Modal = ({ onClose, onEventCreated, onEventEdited, initialTimeSlot, rooms, reservation, initialDate }) => {
    const { t, i18n } = useTranslation();
    const currentDir = i18n.dir();

    const WEEKDAYS = [
        { label: t('weekday_sat'), value: 'SA' },
        { label: t('weekday_sun'), value: 'SU' },
        { label: t('weekday_mon'), value: 'MO' },
        { label: t('weekday_tue'), value: 'TU' },
        { label: t('weekday_wed'), value: 'WE' },
        { label: t('weekday_thu'), value: 'TH' },
        { label: t('weekday_fri'), value: 'FR' },
    ];
    // If editing, prefill with reservation data
    const isEditing = !!reservation;
    const getStart = useCallback(() => {
        const now = moment();
        if (!isEditing) {
            if (initialTimeSlot) {
                return toDateTimeString(`${initialTimeSlot.date}T${initialTimeSlot.startTime}`);
            }
            return toDateTimeString(now);
        }
        if (reservation?.occurrence_start) {
            return toDateTimeString(reservation.occurrence_start);
        }
        if (reservation?.date && reservation?.startTime) {
            return toDateTimeString(`${reservation.date}T${reservation.startTime}`);
        }
        if (reservation?.start) {
            return toDateTimeString(reservation.start);
        }
        return toDateTimeString(now);
    }, [isEditing, initialTimeSlot, reservation]);

    const getEnd = useCallback(() => {
        const now = moment();
        if (!isEditing) {
            if (initialTimeSlot) {
                return toDateTimeString(`${initialTimeSlot.date}T${initialTimeSlot.endTime}`);
            }
            return toDateTimeString(now.clone().add(60, 'minutes'));
        }
        if (reservation?.occurrence_end) {
            return toDateTimeString(reservation.occurrence_end);
        }
        if (reservation?.date && reservation?.endTime) {
            return toDateTimeString(`${reservation.date}T${reservation.endTime}`);
        }
        if (reservation?.end) {
            return toDateTimeString(reservation.end);
        }
        return toDateTimeString(now.clone().add(60, 'minutes'));
    }, [isEditing, initialTimeSlot, reservation]);

    const getInitialResourceId = useCallback(() => {
        if (isEditing) {
            return reservation.roomId || reservation.resource_id;
        }
        if (initialTimeSlot) {
            return initialTimeSlot.roomId;
        }
        if (rooms && rooms.length > 0) {
            return rooms[0].id;
        }
        return null;
    }, [isEditing, initialTimeSlot, reservation, rooms]);

    const [formData, setFormData] = useState({
        resource_id: getInitialResourceId(),
        name: isEditing ? reservation.title : t('default_event_name'),
        start: getStart(),
        end: getEnd(),
        description: isEditing ? reservation.description : '',
        recurring: isEditing ? reservation.recurring : null,
    });

    // Hold state for ephemeral "reserving" indicator
    const holdIdRef = useRef(null);
    const lastHoldContextRef = useRef({ date: null, resourceId: null });
    const sendHold = useCallback((action, startStr, endStr, resourceId, dateOverride) => {
        try {
            // Always prefer the current start (or override) to derive the date; fall back only if not present
            const dateStr = dateOverride || moment(startStr || formData.start).format('YYYY-MM-DD');
            const startMin = moment(startStr || formData.start).hours() * 60 + moment(startStr || formData.start).minutes();
            const endMin = moment(endStr || formData.end).hours() * 60 + moment(endStr || formData.end).minutes();
            const resId = Number(resourceId ?? formData.resource_id);
            reservationsWS.sendHold(action, {
                hold_id: holdIdRef.current,
                date: dateStr,
                resource_id: resId,
                start_minute: Math.min(startMin, endMin),
                end_minute: Math.max(startMin, endMin),
            });
            if (action !== 'hold_end') {
                lastHoldContextRef.current = { date: dateStr, resourceId: resId };
            }
        } catch (_) {}
    }, [formData.start, formData.end, formData.resource_id]);

    useEffect(() => {
        if (isEditing) {
            setFormData({
                resource_id: reservation.roomId || reservation.resource_id,
                name: reservation.title,
                start: getStart(),
                end: getEnd(),
                description: reservation.description,
                recurring: reservation.recurring && reservation.recurring !== '' ? reservation.recurring : null,
            });
            if (reservation.recurring && reservation.recurring !== '') {
                setDoesRepeat(true);
                try {
                    const ruleObj = RRule.parseString(reservation.recurring);
                    if (ruleObj.freq !== undefined) setFrequency(RRule.FREQUENCIES[ruleObj.freq].toUpperCase());
                    if (ruleObj.interval) setInterval(Number(ruleObj.interval));
                    if (ruleObj.byweekday) {
                        const days = Array.isArray(ruleObj.byweekday)
                            ? ruleObj.byweekday.map(d => d.toString().slice(0,2).toUpperCase())
                            : [ruleObj.byweekday.toString().slice(0,2).toUpperCase()];
                        setByWeekDays(days);
                    }
                    if (ruleObj.count) {
                        setEndCondition('after');
                        setCount(Number(ruleObj.count));
                    } else if (ruleObj.until) {
                        setEndCondition('until');
                        setUntil(ruleObj.until.toISOString().slice(0, 10));
                    } else {
                        setEndCondition('never');
                    }
                } catch (e) {
                    // ignore
                }
            } else {
                setDoesRepeat(false);
                setFrequency('');
                setByWeekDays([]);
                setInterval(1);
                setEndCondition('never');
                setCount(1);
                setUntil('');
            }
        } else if (initialTimeSlot) {
            setFormData((prev) => ({
                ...prev,
                start: getStart(),
                end: getEnd(),
            }));
            setDoesRepeat(false);
            setFrequency('');
            setByWeekDays([]);
            setInterval(1);
            setEndCondition('never');
            setCount(1);
            setUntil('');
        }
    }, [reservation, initialTimeSlot, isEditing, getStart, getEnd]);

    useEffect(() => {
        if (!isEditing && !initialTimeSlot && rooms && rooms.length > 0) {
            setFormData((prev) => {
                const currentResourceId = prev.resource_id;
                const isValidResourceId = rooms.some(room => room.id === currentResourceId);
                if (!isValidResourceId) {
                    return {
                        ...prev,
                        resource_id: rooms[0].id,
                    };
                }
                return prev;
            });
        }
    }, [rooms, isEditing, initialTimeSlot]);

    // Send hold_start on mount, hold_update on form changes, hold_end on unmount/close
    useEffect(() => {
        // start
        holdIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        sendHold('hold_start');
        // initialize last context
        lastHoldContextRef.current = {
            date: moment(formData.start).format('YYYY-MM-DD'),
            resourceId: formData.resource_id,
        };
        return () => {
            // end
            if (holdIdRef.current) {
                sendHold('hold_end');
                holdIdRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!holdIdRef.current) return;
        sendHold('hold_update');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.resource_id, formData.start, formData.end, formData.name]);

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (holdIdRef.current) {
                    sendHold('hold_end');
                    holdIdRef.current = null;
                }
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose, sendHold]);

    // When the date portion of start changes, move the hold to the new date immediately
    useEffect(() => {
        if (!holdIdRef.current) return;
        const currentDate = moment(formData.start).format('YYYY-MM-DD');
        const prev = lastHoldContextRef.current;
        if (prev.date && prev.date !== currentDate) {
            // End old-date hold explicitly, then start on the new date
            sendHold('hold_end', undefined, undefined, prev.resourceId, prev.date);
            setTimeout(() => sendHold('hold_start'), 0);
        }
        lastHoldContextRef.current = { date: currentDate, resourceId: formData.resource_id };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.start]);

    // Recurrence UI state
    const [doesRepeat, setDoesRepeat] = useState(false);
    const [frequency, setFrequency] = useState(''); // DAILY, WEEKLY, MONTHLY
    const [byWeekDays, setByWeekDays] = useState([]); // For weekly
    const [interval, setInterval] = useState(1);
    const [endCondition, setEndCondition] = useState('never'); // never, after, until
    const [count, setCount] = useState(1); // for 'after'
    const [until, setUntil] = useState(''); // for 'until'
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'start') {
            const prevDateStr = moment(formData.start).format('YYYY-MM-DD');
            const nextStr = toDateTimeString(value);
            const nextDateStr = moment(nextStr).format('YYYY-MM-DD');
            // If date changed, explicitly move hold to the new date immediately
            if (holdIdRef.current && prevDateStr !== nextDateStr) {
                const prevCtx = lastHoldContextRef.current;
                if (prevCtx?.date) {
                    sendHold('hold_end', undefined, undefined, prevCtx.resourceId, prevCtx.date);
                }
                // defer start to ensure end is processed first
                setTimeout(() => sendHold('hold_start', nextStr, formData.end, formData.resource_id, nextDateStr), 0);
            } else if (holdIdRef.current) {
                // same day, just update window
                sendHold('hold_update', nextStr, formData.end);
            }
            setFormData((prev) => ({ ...prev, start: nextStr }));
        } else if (name === 'end') {
            const nextStr = toDateTimeString(value);
            if (holdIdRef.current) {
                sendHold('hold_update', formData.start, nextStr);
            }
            setFormData((prev) => ({ ...prev, end: nextStr }));
        } else if (name === 'room') {
            const selectedRoom = rooms.find(room => room.name === value);
            const nextResourceId = selectedRoom ? selectedRoom.id : '';
            const prevResourceId = formData.resource_id;
            // If changing room by label, end previous hold and start/update for new room
            if (holdIdRef.current && nextResourceId && prevResourceId && Number(prevResourceId) !== Number(nextResourceId)) {
                sendHold('hold_end', undefined, undefined, prevResourceId);
                // small defer to ensure receivers process end first
                setTimeout(() => sendHold('hold_start', undefined, undefined, nextResourceId), 0);
            }
            setFormData(prev => ({
                ...prev,
                resource_id: nextResourceId,
            }));
        } else if (name === 'resource_id') {
            const nextResourceId = Number(value);
            // End previous hold on old room, and start on the new room
            if (holdIdRef.current) {
                const prevResourceId = formData.resource_id;
                if (prevResourceId && Number(prevResourceId) !== Number(nextResourceId)) {
                    sendHold('hold_end', undefined, undefined, prevResourceId);
                    setTimeout(() => sendHold('hold_start', undefined, undefined, nextResourceId), 0);
                }
            }
            setFormData(prev => ({ ...prev, resource_id: nextResourceId }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Recurrence UI handlers
    const handleFrequencyChange = (e) => {
        setFrequency(e.target.value);
        setByWeekDays([]); // Reset days if switching frequency
    };
    const handleWeekdayToggle = (day) => {
        setByWeekDays(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day]
        );
    };
    const handleEndConditionChange = (e) => {
        setEndCondition(e.target.value);
        if (e.target.value !== 'after') setCount(1);
        if (e.target.value !== 'until') setUntil('');
    };

    // RRULE string generator
    const buildRRule = () => {
        if (!frequency) return null;
        let rrule = `FREQ=${frequency}`;
        if (interval && interval > 1) rrule += `;INTERVAL=${interval}`;
        if (frequency === 'WEEKLY') {
            if (byWeekDays.length === 0) {
                // Prevent creating a weekly recurrence with no days selected
                setError(t('modal_error_weekly'));
                return null;
            }
            rrule += `;BYDAY=${byWeekDays.join(',')}`;
        }
        if (endCondition === 'after' && count > 0) {
            rrule += `;COUNT=${count}`;
        } else if (endCondition === 'until' && until) {
            // Format until as YYYYMMDDTHHmmssZ (UTC)
            const untilMoment = moment(until).endOf('day').utc();
            rrule += `;UNTIL=${untilMoment.format('YYYYMMDD[T]HHmmss[Z]')}`;
        }
        return rrule;
    };

    // Update formData.recurring when recurrence UI changes
    useEffect(() => {
        if (doesRepeat) {
            setFormData(prev => ({
                ...prev,
                recurring: buildRRule(),
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                recurring: null,
            }));
        }
        // eslint-disable-next-line
    }, [doesRepeat, frequency, byWeekDays, interval, endCondition, count, until]);

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);

        const start = moment(formData.start).format('YYYY-MM-DDTHH:mm:ss');
        const end = moment(formData.end).format('YYYY-MM-DDTHH:mm:ss');

        const eventData = {
            resource_id: formData.resource_id,
            title: formData.name,
            start: start,
            end: end,
            description: formData.description,
            recurring: formData.recurring
        };

        try {
            const token = localStorage.getItem('token');
            if (isEditing) {
                // PUT to update
                const resp = await axios.put(`${API_BASE_URL}/api/reservations/${reservation.id}/`,
                    eventData,
                    {
                        headers: {
                            'Authorization': `Token ${token}`,
                            'Content-Type': 'application/json',
                        }
                    }
                );
                if (onEventEdited) onEventEdited(resp?.data);
            } else {
                // POST to create
                const resp = await axios.post(`${API_BASE_URL}/api/reservations/`,
                    eventData,
                    {
                        headers: {
                            'Authorization': `Token ${token}`,
                            'Content-Type': 'application/json',
                        }
                    }
                );
                if (onEventCreated) onEventCreated(resp?.data);
            }
            // Close hold on successful save
            if (holdIdRef.current) {
                sendHold('hold_end');
                holdIdRef.current = null;
            }
            onClose();
        } catch (error) {
            setError(
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.non_field_errors ||
                t('modal_error_save')
            );
        }
    };

    const handleOutsideClick = (e) => {
        if (e.target === e.currentTarget) {
            if (holdIdRef.current) {
                sendHold('hold_end');
                holdIdRef.current = null;
            }
            onClose();
        }
    };

    const formControlStyle = {
        width: '100%',
        padding: '10px',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        marginTop: '5px',
        boxSizing: 'border-box',
        background: 'var(--surface)',
        color: 'var(--text)'
    };

    const formGroupStyle = {
        marginBottom: '15px',
        width: '100%',
    };

    const labelStyle = {
        fontWeight: 'bold',
        color: 'var(--text)',
        display: 'block',
    };

    return (
        <div onClick={handleOutsideClick} className="modal-overlay">
            <div
                className="modal-card"
                style={{ width: 'min(520px, 92vw)' }}
                dir={currentDir}
            >
                <h2 style={{ marginTop: 0, fontSize: '24px', color: 'var(--text)', textAlign: 'center' }}>
                    {isEditing ? t('modal_edit_event') : t('modal_create_event')}
                </h2>

                {error && (
                    <div
                        className="card"
                        style={{
                            background: 'color-mix(in oklab, var(--color-danger) 12%, var(--surface))',
                            color: 'var(--text)',
                            border: '1px solid',
                            borderColor: 'color-mix(in oklab, var(--color-danger) 35%, var(--border))',
                            padding: '10px',
                            borderRadius: '5px',
                            marginBottom: '15px',
                            fontSize: '14px',
                            textAlign: 'center'
                        }}
                    >
                        {error}
                    </div>
                )}

                <form onSubmit={handleSave}>
                    <div style={formGroupStyle}>
                        <label style={labelStyle}>{t('modal_event_name')}</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            style={formControlStyle}
                            required
                        />
                    </div>

                    <div style={formGroupStyle}>
                        <label style={labelStyle}>{t('modal_room')}</label>
                        <select
                            name="resource_id"
                            value={formData.resource_id}
                            onChange={handleChange}
                            style={formControlStyle}
                            required
                        >
                            {rooms.map(room => (
                                <option key={room.id} value={room.id}>
                                    {room.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={formGroupStyle}>
                        {/* <DatePicker wrapperClassName="datePicker"
                              selected={formData.start}
                              onChange={(date) => {
                                 formData.start = date
                                 handleChange( {target : {name: "start", value: formData.start}})
                              }}
                              showTimeSelect
                              timeIntervals={15}
                              dateFormat="yyyy-MM-dd HH:mm"
                        /> */}
                        <CustomDatePicker
                            key={`start-${formData.resource_id}-${formData.start}`}
                            selected={toDateObj(formData.start)}
                            onChange={handleChange}
                            label={t('modal_start_time')}
                            name="start"
                        />

                        {/* <input
                            type="datetime-local"
                            name="start"
                            value={formData.start}
                            onChange={handleChange}
                            style={formControlStyle}
                        /> */}
                    </div>

                    <div style={formGroupStyle}>
                        <CustomDatePicker
                            key={`end-${formData.resource_id}-${formData.end}`}
                            selected={toDateObj(formData.end)}
                            onChange={handleChange}
                            label={t('modal_end_time')}
                            name="end"
                        />
                        {/* <label style={labelStyle}>End Time:</label>
                        <input
                            type="datetime-local"
                            name="end"
                            value={formData.end}
                            onChange={handleChange}
                            style={formControlStyle}
                        /> */}
                    </div>

                    <div style={formGroupStyle}>
                        <label style={labelStyle}>{t('modal_description')}</label>
                        <input
                            type="text"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            style={formControlStyle}
                        />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ fontWeight: 'bold', color: '#555', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                checked={doesRepeat}
                                onChange={e => setDoesRepeat(e.target.checked)}
                                style={{ cursor: 'pointer', margin: 0 }}
                            />
                            {t('modal_repeat')}
                        </label>
                    </div>
                    {doesRepeat && (
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ fontWeight: 'bold', color: '#555' }}>{t('modal_frequency')}</label>
                            <select
                                value={frequency}
                                onChange={handleFrequencyChange}
                                style={formControlStyle}
                            >
                                <option value="">{t('modal_frequency_select')}</option>
                                <option value="DAILY">{t('modal_frequency_daily')}</option>
                                <option value="WEEKLY">{t('modal_frequency_weekly')}</option>
                                <option value="MONTHLY">{t('modal_frequency_monthly')}</option>
                            </select>
                            {frequency === 'WEEKLY' && (
                                <div style={{ marginTop: '10px' }}>
                                    <span style={{ fontWeight: 'bold', color: '#555' }}>{t('modal_repeat_on')}</span>
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '5px', flexWrap: 'wrap' }}>
                                        {WEEKDAYS.map(day => (
                                            <label key={day.value} style={{ fontWeight: 'normal', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={byWeekDays.includes(day.value)}
                                                    onChange={() => handleWeekdayToggle(day.value)}
                                                    style={{ margin: 0 }}
                                                />
                                                {day.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div style={{ marginTop: '10px' }}>
                                <label style={{ fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '5px' }}>{t('modal_interval')}</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="number"
                                        min={1}
                                        value={interval}
                                        onChange={e => setInterval(Number(e.target.value))}
                                        style={{ ...formControlStyle, width: '80px', marginTop: 0 }}
                                    />
                                    <span style={{ color: '#888', fontSize: '14px' }}>
                                        {t('modal_interval_hint')}
                                    </span>
                                </div>
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                <label style={{ fontWeight: 'bold', color: '#555', display: 'block', marginBottom: '5px' }}>{t('modal_ends')}</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <select
                                        value={endCondition}
                                        onChange={handleEndConditionChange}
                                        style={{ ...formControlStyle, width: 'auto', minWidth: '200px', marginTop: 0 }}
                                    >
                                        <option value="never">{t('modal_ends_never')}</option>
                                        <option value="after">{t('modal_ends_after')}</option>
                                        <option value="until">{t('modal_ends_until')}</option>
                                    </select>
                                    {endCondition === 'after' && (
                                        <input
                                            type="number"
                                            min={1}
                                            value={count}
                                            onChange={e => setCount(Number(e.target.value))}
                                            style={{ ...formControlStyle, width: '80px', marginTop: 0 }}
                                        />
                                    )}
                                    {endCondition === 'until' && (
                                        <input
                                            type="date"
                                            value={until}
                                            onChange={e => setUntil(e.target.value)}
                                            style={{ ...formControlStyle, width: '180px', marginTop: 0 }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">{t('modal_cancel')}</button>
                        <button type="submit" className="btn btn-primary">{t('modal_save')}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Modal;
